import {
  Box,
  Button,
  CircularProgress,
  Grid,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import WarningModal from '../../../../../packages/common/shared-ui/src/components/WarningModal';
import { messages } from '../../../../../packages/common/shared-ui/src/i18n/messages';
import { useTranslation } from '../../../../../packages/common/utils/getLocalString';
import CalendarException from '../CalendarException';
import CalendarExceptionCreate from '../CalendarExceptionCreate';
import CalendarYearSwitcher from '../CalendarYearSwitcher';
import CustomCalendar from '../CustomCalendar';

// Original class: CalendarBodyRoot
const StyledCalendarBodyRoot = styled(Grid)(({ theme }) => ({
  backgroundColor: '#ffffff',
  padding: theme.spacing(5, 4, 1, 10),
  borderRadius: theme.spacing(4),
  [theme.breakpoints.down('lg')]: {
    padding: theme.spacing(4, 0, 4, 4),
  },
  [theme.breakpoints.down('xs')]: {
    padding: theme.spacing(5, 2.5),
  },
}));

const CalendarBody = ({
  values,
  workHours,
  setFieldValue,
  errors,
  handleChange,
  customDays,
  customExclusions,
  isLoading,
  handleBackYear,
  handleForwardYear,
  year,
  handleUpdateCalendars,
  removeException,
  isCreateModalOpen,
  toggleCreateModalOpen,
  submitForm,
  handleUpdateRequests,
}) => {
  const getLocalString = useTranslation();
  const isSmallScreen = useMediaQuery(theme =>
    theme.breakpoints.maxWidth('lg'),
  );
  const [newExceptionDate, setNewExceptionDate] = useState(dayjs().format());
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [actionDirection, setActionDirection] = useState('');

  const { current: openWarningModal } = useRef(direction => {
    setActionDirection(direction);
    setIsWarningModalOpen(true);
  });

  const { current: closeWarningModal } = useRef(() => {
    setIsWarningModalOpen(false);
  });

  const { current: formattedMonths } = useRef(
    new Array(12).fill(null).map((month, i) => {
      return dayjs().month(i).format('MM');
    }),
  );

  const { current: getCurrentDay } = useRef((date, days) => {
    if (days.hasOwnProperty(dayjs(date).format('YYYY-MM-DD'))) {
      return days[dayjs(date).format('YYYY-MM-DD')];
    }
    return { date };
  });

  const handleToggleExclusionMenu = useCallback(
    date => {
      if (!date) {
        return toggleCreateModalOpen(false);
      }
      setNewExceptionDate(date);
      setFieldValue('date', dayjs(date).format('YYYY-MM-DD'));
      return toggleCreateModalOpen(status => !status);
    },
    [toggleCreateModalOpen, setFieldValue],
  );

  const handleOpenExclusionMenu = useCallback(
    e => {
      handleToggleExclusionMenu(dayjs().set('year', year).format());
    },
    [handleToggleExclusionMenu, year],
  );

  const handleToggleYear = useCallback(
    async e => {
      if (actionDirection) {
        if (e.currentTarget.value === 'cancel') {
          setActionDirection('');
          return closeWarningModal();
        }
        if (e.currentTarget.value === 'delete') {
          setFieldValue('newCustomDates', []);
        }
        if (e.currentTarget.value === 'save') {
          await submitForm(year);
        }
        if (actionDirection === 'left') {
          closeWarningModal();
          setActionDirection('');
          handleBackYear();
          return setFieldValue('newCustomDates', []);
        }
        if (actionDirection === 'right') {
          closeWarningModal();
          setActionDirection('');
          handleForwardYear();
          return setFieldValue('newCustomDates', []);
        }
        return handleUpdateRequests();
      }
      if (!e.currentTarget.attributes.direction.nodeValue) {
        return;
      }
      if (values.newCustomDates.length > 0 && !isWarningModalOpen) {
        return openWarningModal(e.currentTarget.attributes.direction.nodeValue);
      }
      if (e.currentTarget.attributes.direction.nodeValue === 'left') {
        return handleBackYear();
      }
      if (e.currentTarget.attributes.direction.nodeValue === 'right') {
        return handleForwardYear();
      }
    },
    [
      actionDirection,
      closeWarningModal,
      handleBackYear,
      handleForwardYear,
      handleUpdateRequests,
      isWarningModalOpen,
      openWarningModal,
      setFieldValue,
      submitForm,
      values.newCustomDates.length,
      year,
    ],
  );

  return (
    <>
      <StyledCalendarBodyRoot container direction='column'>
        <WarningModal
          isWarningModalOpen={isWarningModalOpen}
          openWarningModal={openWarningModal}
          closeWarningModal={closeWarningModal}
          handleToggle={handleToggleYear}
          title={getLocalString('messages', 'saveChanges')}
        />
        <CalendarExceptionCreate
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          handleChange={handleChange}
          handleToggleExclusionMenu={handleToggleExclusionMenu}
          isCreateModalOpen={isCreateModalOpen}
          newExceptionDate={newExceptionDate}
          setNewExceptionDate={setNewExceptionDate}
          handleUpdateCalendars={handleUpdateCalendars}
        />
        <Grid
          container
          sx={{
            alignItems: 'flex-end',
          }}
        >
          <CalendarYearSwitcher
            year={year}
            handleToggleYear={handleToggleYear}
            actionDirection={actionDirection}
          />
          <Box
            sx={{
              marginBottom: 4.5,
              marginLeft: 5,
            }}
          >
            <Button
              variant='defaultBluePrimary'
              onClick={handleOpenExclusionMenu}
              value={newExceptionDate}
              disableRipple
            >
              {getLocalString('buttons', 'add')}
            </Button>
          </Box>
        </Grid>
        <Grid
          container
          wrap={isSmallScreen ? 'wrap-reverse' : 'nowrap'}
          sx={{
            justifyContent: 'center',
          }}
        >
          <Grid
            container
            wrap='wrap'
            sx={{
              alignItems: 'center',
              flexGrow: '1',
              justifyContent: isSmallScreen ? 'center' : 'unset',
            }}
          >
            {!isLoading ? (
              formattedMonths.map(month => {
                return (
                  <CustomCalendar
                    year={year}
                    month={month}
                    key={month}
                    customDays={customDays}
                    handleToggleExclusionMenu={handleToggleExclusionMenu}
                    getCurrentDay={getCurrentDay}
                    setFieldValue={setFieldValue}
                    workHours={workHours}
                  />
                );
              })
            ) : (
              <Grid
                container
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100vh',
                }}
              >
                <CircularProgress color='secondary' />
              </Grid>
            )}
          </Grid>
          {customExclusions && (
            <CalendarException
              exclusions={customExclusions}
              removeException={removeException}
            />
          )}
        </Grid>
      </StyledCalendarBodyRoot>
    </>
  );
};

export default memo(CalendarBody);
